/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const defaultRoom = {
  business_unit_id: "",
  room_category_id: "",
  room_number: "",
  floor: 1,
  sq_meters: 0,
  num_beds: 1,
  bed_type: "",
  features: "",
  view: "",
  description: "",
};


function RoomsList() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showNewPopup, setShowNewPopup] = useState(false);
  const [newRoom, setNewRoom] = useState(defaultRoom);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const [businessUnits, setBusinessUnits] = useState([]);
  const [roomCategories, setRoomCategories] = useState([]);

  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editRoom, setEditRoom] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState({
    id: [],
    roomNumbers: [],
    floors: [],
    categories: [],
    businessUnits: [],
    sqMeters: [],
    beds: [],
    bedTypes: [],
    views: [],
    features: "",
    description: "",
  });
  const [activeFilter, setActiveFilter] = useState(null); // { key, rect }
  const [filterOptionQuery, setFilterOptionQuery] = useState("");
  const filterPopoverRef = useRef(null);
  const tableWrapRef = useRef(null);
  const tableRef = useRef(null);
  const tableZoomRef = useRef(1);
  const [tableZoom, setTableZoom] = useState(1);

  useEffect(() => {
    tableZoomRef.current = tableZoom;
  }, [tableZoom]);

  const recomputeTableZoom = useCallback(() => {
    const wrap = tableWrapRef.current;
    const table = tableRef.current;

    if (!wrap || !table) {
      if (tableZoomRef.current !== 1) setTableZoom(1);
      return;
    }

    const wrapWidth = wrap.clientWidth || wrap.getBoundingClientRect().width;
    if (!wrapWidth) return;

    const currentZoom = tableZoomRef.current || 1;
    const measuredWidth = table.getBoundingClientRect().width;
    if (!measuredWidth) return;

    const naturalWidth = measuredWidth / currentZoom;
    if (!naturalWidth) return;

    const ratio = wrapWidth / naturalWidth;
    if (ratio >= 1) {
      if (currentZoom !== 1) setTableZoom(1);
      return;
    }

    const safeWrapWidth = Math.max(0, wrapWidth - 2);
    const nextZoom = safeWrapWidth / naturalWidth;
    const floored = Math.floor(nextZoom * 1000) / 1000;

    if (Math.abs(floored - currentZoom) > 0.005) {
      setTableZoom(floored);
    }
  }, []);

  useEffect(() => {
    const wrap = tableWrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") return undefined;

    const ro = new ResizeObserver(() => recomputeTableZoom());
    ro.observe(wrap);

    return () => ro.disconnect();
  }, [recomputeTableZoom]);

  const businessUnitById = useMemo(() => {
    const map = new Map();
    businessUnits.forEach((bu) => {
      map.set(String(bu.id), bu);
    });
    return map;
  }, [businessUnits]);

  const roomCategoryById = useMemo(() => {
    const map = new Map();
    roomCategories.forEach((cat) => {
      map.set(String(cat.id), cat);
    });
    return map;
  }, [roomCategories]);

  const filteredRooms = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    const idSet = new Set((columnFilters.id || []).map((x) => Number(x)));
    const roomNumbersSet = new Set(
      (columnFilters.roomNumbers || []).map((x) => String(x).toLowerCase())
    );
    const floorsSet = new Set((columnFilters.floors || []).map((x) => Number(x)));
    const categoriesSet = new Set((columnFilters.categories || []).map((x) => String(x)));
    const buSet = new Set((columnFilters.businessUnits || []).map((x) => String(x)));
    const sqMetersSet = new Set((columnFilters.sqMeters || []).map((x) => Number(x)));
    const bedsSet = new Set((columnFilters.beds || []).map((x) => Number(x)));
    const bedTypesSet = new Set(
      (columnFilters.bedTypes || []).map((x) => String(x).toLowerCase())
    );
    const viewsSet = new Set((columnFilters.views || []).map((x) => String(x).toLowerCase()));

    const featuresNeedle = String(columnFilters.features || "").trim().toLowerCase();
    const descriptionNeedle = String(columnFilters.description || "").trim().toLowerCase();

    return rooms.filter((room) => {
      const roomIdNum = Number(room.id);
      const roomNumberStr = String(room.room_number || "");
      const floorNum = Number(room.floor);
      const categoryIdStr = String(room.room_category_id ?? "");
      const buIdStr = String(room.business_unit_id ?? "");
      const sqMetersNum = Number(room.sq_meters);
      const bedsNum = Number(room.num_beds);
      const bedTypeStr = String(room.bed_type || "");
      const featuresStr = String(room.features || "");
      const viewStr = String(room.view || "");
      const descStr = String(room.description || "");

      if (idSet.size && !idSet.has(roomIdNum)) return false;
      if (roomNumbersSet.size && !roomNumbersSet.has(roomNumberStr.toLowerCase())) return false;
      if (floorsSet.size && !floorsSet.has(floorNum)) return false;
      if (categoriesSet.size && !categoriesSet.has(categoryIdStr)) return false;
      if (buSet.size && !buSet.has(buIdStr)) return false;
      if (sqMetersSet.size && !sqMetersSet.has(sqMetersNum)) return false;
      if (bedsSet.size && !bedsSet.has(bedsNum)) return false;
      if (bedTypesSet.size && !bedTypesSet.has(bedTypeStr.toLowerCase())) return false;
      if (viewsSet.size && !viewsSet.has(viewStr.toLowerCase())) return false;
      if (featuresNeedle && !featuresStr.toLowerCase().includes(featuresNeedle)) return false;
      if (descriptionNeedle && !descStr.toLowerCase().includes(descriptionNeedle)) return false;

      if (!q) return true;

      const bu = businessUnitById.get(String(room.business_unit_id));
      const cat = roomCategoryById.get(String(room.room_category_id));

      const haystack = [
        room.id,
        room.room_number,
        room.floor,
        cat?.name,
        room.room_category_id,
        bu?.name,
        room.business_unit_id,
        room.sq_meters,
        room.num_beds,
        room.bed_type,
        room.features,
        room.view,
        room.description,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [businessUnitById, columnFilters, roomCategoryById, rooms, searchQuery]);

  useLayoutEffect(() => {
    recomputeTableZoom();
  }, [loading, filteredRooms.length, recomputeTableZoom]);

  const openEditPopup = (room) => {
    setEditRoom(room);
    setShowEditPopup(true);
    setEditError("");
  };

  const columnIsFiltered = (key) => {
    const value = columnFilters[key];
    return Array.isArray(value) ? value.length > 0 : String(value || "").trim().length > 0;
  };

  const roomNumberStats = useMemo(() => {
    const counts = new Map(); // lower -> count
    const display = new Map(); // lower -> display

    rooms.forEach((room) => {
      const roomNumber = String(room.room_number || "").trim();
      if (!roomNumber) return;
      const key = roomNumber.toLowerCase();
      counts.set(key, (counts.get(key) || 0) + 1);
      if (!display.has(key)) display.set(key, roomNumber);
    });

    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
    const options = Array.from(display.entries())
      .map(([key, name]) => ({ key, name }))
      .sort((a, b) => collator.compare(a.name, b.name));

    return { options, counts };
  }, [rooms]);

  const bedTypeStats = useMemo(() => {
    const counts = new Map(); // lower -> count
    const display = new Map(); // lower -> display

    rooms.forEach((room) => {
      const bedType = String(room.bed_type || "").trim();
      if (!bedType) return;
      const key = bedType.toLowerCase();
      counts.set(key, (counts.get(key) || 0) + 1);
      if (!display.has(key)) display.set(key, bedType);
    });

    const options = Array.from(display.entries())
      .map(([key, name]) => ({ key, name }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

    return { options, counts };
  }, [rooms]);

  const viewStats = useMemo(() => {
    const counts = new Map(); // lower -> count
    const display = new Map(); // lower -> display

    rooms.forEach((room) => {
      const view = String(room.view || "").trim();
      if (!view) return;
      const key = view.toLowerCase();
      counts.set(key, (counts.get(key) || 0) + 1);
      if (!display.has(key)) display.set(key, view);
    });

    const options = Array.from(display.entries())
      .map(([key, name]) => ({ key, name }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

    return { options, counts };
  }, [rooms]);

  const columnSubtitle = (key) => {
    const value = columnFilters[key];

    if (Array.isArray(value)) {
      if (!value.length) return "All";

      if (value.length === 1) {
        const only = value[0];

        if (key === "id") return `#${only}`;

        if (key === "businessUnits") {
          return businessUnitById.get(String(only))?.name ?? `ID ${only}`;
        }

        if (key === "categories") {
          return roomCategoryById.get(String(only))?.name ?? `ID ${only}`;
        }

        if (key === "roomNumbers") {
          const k = String(only || "").toLowerCase();
          const displayName = roomNumberStats.options.find((o) => o.key === k)?.name;
          return displayName ?? String(only);
        }

        if (key === "floors") return `Floor ${only}`;
        if (key === "sqMeters") return `${only} m²`;
        if (key === "beds") return String(only);

        if (key === "bedTypes") {
          const k = String(only || "").toLowerCase();
          const displayName = bedTypeStats.options.find((o) => o.key === k)?.name;
          return displayName ?? String(only);
        }

        if (key === "views") {
          const k = String(only || "").toLowerCase();
          const displayName = viewStats.options.find((o) => o.key === k)?.name;
          return displayName ?? String(only);
        }

        return String(only);
      }

      if (key === "id") return `${value.length} IDs`;
      if (key === "roomNumbers") return `${value.length} rooms`;
      if (key === "floors") return `${value.length} floors`;
      if (key === "categories") return `${value.length} categories`;
      if (key === "businessUnits") return `${value.length} units`;
      if (key === "sqMeters") return `${value.length} sizes`;
      if (key === "beds") return `${value.length} values`;
      if (key === "bedTypes") return `${value.length} types`;
      if (key === "views") return `${value.length} views`;
      return `${value.length} selected`;
    }

    const needle = String(value || "").trim();
    if (!needle) return "All";
    if (key === "features") return `Contains: ${needle}`;
    if (key === "description") return `Contains: ${needle}`;
    return needle;
  };

  const closeFilterPopover = () => setActiveFilter(null);

  const toggleFilterPopover = (key, el) => {
    setActiveFilter((prev) => {
      if (prev?.key === key) return null;
      return { key, rect: el.getBoundingClientRect() };
    });
  };

  useEffect(() => {
    setFilterOptionQuery("");
  }, [activeFilter?.key]);

  useEffect(() => {
    if (!activeFilter?.key) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeFilterPopover();
    };

    const onMouseDown = (e) => {
      if (filterPopoverRef.current?.contains(e.target)) return;
      if (e.target?.closest?.("[data-rc-filter-trigger]")) return;
      closeFilterPopover();
    };

    const onResizeOrScroll = () => closeFilterPopover();

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("resize", onResizeOrScroll);
    window.addEventListener("scroll", onResizeOrScroll, true);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("resize", onResizeOrScroll);
      window.removeEventListener("scroll", onResizeOrScroll, true);
    };
  }, [activeFilter?.key]);

  const toggleMultiSelectFilter = (key, value) => {
    setColumnFilters((prev) => {
      const prevArr = Array.isArray(prev[key]) ? prev[key] : [];
      const exists = prevArr.includes(value);
      const nextArr = exists ? prevArr.filter((v) => v !== value) : [...prevArr, value];
      return { ...prev, [key]: nextArr };
    });
  };

  const clearColumnFilter = (key) => {
    setColumnFilters((prev) => ({
      ...prev,
      [key]: Array.isArray(prev[key]) ? [] : "",
    }));
  };

  const idOptions = useMemo(() => {
    const set = new Set();
    rooms.forEach((room) => {
      const n = Number(room.id);
      if (Number.isFinite(n)) set.add(n);
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [rooms]);

  const floorStats = useMemo(() => {
    const counts = new Map();
    rooms.forEach((room) => {
      const n = Number(room.floor);
      if (!Number.isFinite(n)) return;
      counts.set(n, (counts.get(n) || 0) + 1);
    });
    const options = Array.from(counts.keys()).sort((a, b) => a - b);
    return { options, counts };
  }, [rooms]);

  const sqMetersStats = useMemo(() => {
    const counts = new Map();
    rooms.forEach((room) => {
      const n = Number(room.sq_meters);
      if (!Number.isFinite(n)) return;
      counts.set(n, (counts.get(n) || 0) + 1);
    });
    const options = Array.from(counts.keys()).sort((a, b) => a - b);
    return { options, counts };
  }, [rooms]);

  const bedsStats = useMemo(() => {
    const counts = new Map();
    rooms.forEach((room) => {
      const n = Number(room.num_beds);
      if (!Number.isFinite(n)) return;
      counts.set(n, (counts.get(n) || 0) + 1);
    });
    const options = Array.from(counts.keys()).sort((a, b) => a - b);
    return { options, counts };
  }, [rooms]);

  const businessUnitStats = useMemo(() => {
    const counts = new Map();
    rooms.forEach((room) => {
      const id = String(room.business_unit_id ?? "").trim();
      if (!id) return;
      counts.set(id, (counts.get(id) || 0) + 1);
    });

    const map = new Map();
    businessUnits.forEach((bu) => {
      const id = String(bu.id);
      map.set(id, { id, name: String(bu.name || "").trim() || `Business Unit ${id}` });
    });
    rooms.forEach((room) => {
      const id = String(room.business_unit_id ?? "").trim();
      if (!id) return;
      if (!map.has(id)) map.set(id, { id, name: `Business Unit ${id}` });
    });

    const options = Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
    );

    return { options, counts };
  }, [businessUnits, rooms]);

  const categoryStats = useMemo(() => {
    const counts = new Map();
    rooms.forEach((room) => {
      const id = String(room.room_category_id ?? "").trim();
      if (!id) return;
      counts.set(id, (counts.get(id) || 0) + 1);
    });

    const map = new Map();
    roomCategories.forEach((cat) => {
      const id = String(cat.id);
      map.set(id, {
        id,
        name: String(cat.name || "").trim() || `Category ${id}`,
        businessUnitId: cat.business_unit_id != null ? String(cat.business_unit_id) : "",
      });
    });
    rooms.forEach((room) => {
      const id = String(room.room_category_id ?? "").trim();
      if (!id) return;
      if (!map.has(id)) map.set(id, { id, name: `Category ${id}`, businessUnitId: "" });
    });

    const options = Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
    );

    return { options, counts };
  }, [roomCategories, rooms]);

  const uiCss = `
    .rc-page {
      width: 100%;
      max-width: 100%;
      margin: 0 auto;
      padding: 0px 18px 14px;
      height: 100%;
      min-height: 0;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
    }

    @media (max-width: 768px) {
      .rc-page {
        padding: 0px 12px 12px;
        height: 100%;
      }
    }

    .rc-card {
      border-radius: 18px;
      border: 1px solid transparent;
      background:
        linear-gradient(180deg, rgba(25, 25, 51, 0.88), rgba(10, 10, 22, 0.88)) padding-box,
        linear-gradient(135deg, rgba(48, 216, 255, 0.45), rgba(168, 85, 247, 0.55), rgba(255, 77, 79, 0.25)) border-box;
      box-shadow: 0 28px 80px rgba(0, 0, 0, 0.55);
      overflow: hidden;
      position: relative;
      flex: 1 1 auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    .rc-card::before {
      content: "";
      position: absolute;
      inset: 0;
      background:
        radial-gradient(720px 260px at 15% 0%, rgba(48, 216, 255, 0.10), transparent 62%),
        radial-gradient(520px 220px at 88% 10%, rgba(168, 85, 247, 0.11), transparent 62%);
      pointer-events: none;
      opacity: 0.9;
    }

    .rc-card > * {
      position: relative;
      z-index: 1;
    }

    .rc-cardHeader {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      padding: 22px 22px 18px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      background: rgba(0, 0, 0, 0.05);
      flex: 0 0 auto;
    }

    @media (max-width: 900px) {
      .rc-cardHeader {
        flex-direction: column;
        align-items: stretch;
      }
    }

    .rc-title {
      margin: 0;
      font-size: 1.45rem;
      font-weight: 900;
      letter-spacing: 0.2px;
      color: rgba(230, 230, 247, 0.94);
    }

    .rc-subtitle {
      margin-top: 6px;
      font-size: 0.95rem;
      color: rgba(230, 230, 247, 0.62);
    }

    .rc-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 10px;
      flex-wrap: wrap;
    }

    .rc-searchInput {
      width: min(420px, 100%);
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.10);
      border-radius: 14px;
      padding: 10px 12px;
      color: rgba(230, 230, 247, 0.92);
      outline: none;
      transition: border-color 120ms ease, box-shadow 120ms ease;
    }

    .rc-searchInput::placeholder {
      color: rgba(230, 230, 247, 0.45);
    }

    .rc-searchInput:focus {
      border-color: rgba(48, 216, 255, 0.55);
      box-shadow: 0 0 0 3px rgba(48, 216, 255, 0.12);
    }

    .rc-btn {
      border: 1px solid rgba(255, 255, 255, 0.14);
      background: rgba(255, 255, 255, 0.05);
      color: rgba(230, 230, 247, 0.88);
      padding: 10px 14px;
      border-radius: 14px;
      font-weight: 800;
      letter-spacing: 0.15px;
      cursor: pointer;
      transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;
    }

    .rc-btn:disabled {
      opacity: 0.65;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }

    .rc-btn--primary {
      border-color: rgba(48, 216, 255, 0.55);
      background: rgba(48, 216, 255, 0.10);
      color: rgba(178, 245, 255, 0.96);
    }

    .rc-btn--primary:hover {
      transform: translateY(-1px);
      box-shadow: 0 18px 30px rgba(48, 216, 255, 0.10);
    }

    .rc-btn--danger {
      border-color: rgba(255, 77, 79, 0.55);
      background: rgba(255, 77, 79, 0.10);
      color: rgba(255, 226, 226, 0.96);
    }

    .rc-btn--danger:hover {
      transform: translateY(-1px);
      box-shadow: 0 18px 30px rgba(255, 77, 79, 0.10);
    }

    .rc-btn--ghost {
      color: rgba(230, 230, 247, 0.86);
      border-color: rgba(255, 255, 255, 0.14);
      background: rgba(255, 255, 255, 0.04);
    }

    .rc-btn--ghost:hover {
      transform: translateY(-1px);
      box-shadow: 0 18px 30px rgba(0, 0, 0, 0.30);
    }

    .rc-tableWrap {
      flex: 1 1 auto;
      min-height: 0;
      overflow: auto;
      scrollbar-gutter: stable;
    }

    .rc-table {
      width: 100%;
      min-width: 0;
      table-layout: fixed;
      border-collapse: separate;
      border-spacing: 0;
    }

    .rc-table thead th {
      position: sticky;
      top: 0;
      z-index: 2;
      padding: 0;
      background: linear-gradient(
        180deg,
        rgba(22, 22, 50, 0.92),
        rgba(12, 12, 28, 0.92)
      );
      backdrop-filter: blur(10px);
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.06),
        inset 0 -1px 0 rgba(0, 0, 0, 0.18);
      overflow: hidden;
    }

    .rc-table thead th:first-child {
      border-top-left-radius: 14px;
    }

    .rc-table thead th:last-child {
      border-top-right-radius: 14px;
    }

    .rc-table thead th:not(:last-child) {
      box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.06),
        inset 0 -1px 0 rgba(0, 0, 0, 0.18),
        inset -1px 0 0 rgba(255, 255, 255, 0.06);
    }

    .rc-thBtn {
      position: relative;
      width: 100%;
      border: none;
      background: transparent;
      padding: 12px 38px 12px 14px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      gap: 3px;
      text-align: left;
      cursor: pointer;
      user-select: none;
      color: rgba(230, 230, 247, 0.82);
      transition: background 120ms ease, color 120ms ease, transform 120ms ease;
      min-height: 56px;
    }

    .rc-thBtn:hover {
      background: rgba(255, 255, 255, 0.03);
    }

    .rc-thBtn:active {
      transform: translateY(0.5px);
    }

    .rc-thBtn::after {
      content: "";
      position: absolute;
      left: 14px;
      right: 14px;
      bottom: 0;
      height: 2px;
      border-radius: 999px;
      background: linear-gradient(
        90deg,
        rgba(48, 216, 255, 0.0),
        rgba(48, 216, 255, 0.55),
        rgba(168, 85, 247, 0.55),
        rgba(255, 77, 79, 0.18)
      );
      opacity: 0.18;
      transition: opacity 120ms ease;
    }

    .rc-thBtn:hover::after {
      opacity: 0.55;
    }

    .rc-thBtn:focus-visible {
      outline: 2px solid rgba(48, 216, 255, 0.55);
      outline-offset: 3px;
      border-radius: 12px;
    }

    .rc-thBtn--right {
      align-items: flex-end;
      text-align: right;
    }

    .rc-thLabel {
      font-weight: 900;
      letter-spacing: 0.15px;
      color: rgba(230, 230, 247, 0.92);
      font-size: 0.95rem;
      line-height: 1.1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;
    }

    .rc-thSub {
      font-size: 0.78rem;
      font-weight: 700;
      color: rgba(230, 230, 247, 0.55);
      line-height: 1.2;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;
    }

    .rc-thDot {
      position: absolute;
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: rgba(230, 230, 247, 0.22);
      box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.15);
      transition: background 120ms ease, box-shadow 120ms ease;
    }

    .rc-thDot--on {
      background: rgba(48, 216, 255, 0.85);
      box-shadow: 0 0 0 2px rgba(48, 216, 255, 0.18);
    }

    .rc-thBtn--filtered {
      color: rgba(178, 245, 255, 0.92);
    }

    .rc-thBtn--filtered .rc-thLabel {
      color: rgba(178, 245, 255, 0.98);
    }

    .rc-thBtn--filtered .rc-thSub {
      color: rgba(178, 245, 255, 0.70);
    }

    .rc-thBtn--filtered::after {
      opacity: 0.75;
    }

    .rc-thBtn--open {
      background: rgba(48, 216, 255, 0.045);
    }

    .rc-thBtn--open::after {
      opacity: 0.75;
    }

    .rc-table tbody td {
      padding: 14px 14px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.07);
      color: rgba(230, 230, 247, 0.88);
      vertical-align: middle;
    }

    .rc-table tbody tr {
      background: rgba(255, 255, 255, 0.02);
      transition: background 120ms ease, transform 120ms ease, box-shadow 120ms ease;
    }

    .rc-table tbody tr:nth-child(even) {
      background: rgba(255, 255, 255, 0.015);
    }

    .rc-table tbody tr:hover {
      background: rgba(48, 216, 255, 0.055);
      transform: translateY(-1px);
      box-shadow: 0 18px 50px rgba(0, 0, 0, 0.25);
    }

    .rc-idPill {
      display: inline-flex;
      align-items: center;
      padding: 4px 10px;
      border-radius: 999px;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.10);
      font-variant-numeric: tabular-nums;
      color: rgba(230, 230, 247, 0.86);
    }

    .rc-buCell {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    .rc-buName {
      font-weight: 700;
      color: rgba(230, 230, 247, 0.92);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .rc-buMeta {
      font-size: 0.78rem;
      color: rgba(230, 230, 247, 0.52);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .rc-nameBtn {
      all: unset;
      display: block;
      cursor: pointer;
      font-weight: 800;
      color: rgba(48, 216, 255, 0.95);
      transition: color 120ms ease, text-shadow 120ms ease;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .rc-nameBtn:hover {
      color: rgba(178, 245, 255, 1);
      text-shadow: 0 0 24px rgba(48, 216, 255, 0.35);
    }

    .rc-nameBtn:focus-visible {
      outline: 2px solid rgba(48, 216, 255, 0.7);
      outline-offset: 3px;
      border-radius: 6px;
    }

    .rc-desc {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      color: rgba(230, 230, 247, 0.76);
      max-width: 520px;
    }

    .rc-muted {
      color: rgba(230, 230, 247, 0.40);
    }

    .rc-pill {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 4px 10px;
      border-radius: 999px;
      border: 1px solid rgba(255, 255, 255, 0.10);
      background: rgba(255, 255, 255, 0.03);
      font-variant-numeric: tabular-nums;
      min-width: 44px;
    }

    .rc-numCell {
      text-align: right;
    }

    .rc-filterPopover {
      position: fixed;
      z-index: 3000;
      width: min(380px, calc(100vw - 24px));
      border-radius: 16px;
      border: 1px solid transparent;
      background:
        linear-gradient(180deg, rgba(25, 25, 51, 0.96), rgba(10, 10, 22, 0.96)) padding-box,
        linear-gradient(135deg, rgba(48, 216, 255, 0.45), rgba(168, 85, 247, 0.50), rgba(255, 77, 79, 0.28)) border-box;
      box-shadow: 0 28px 90px rgba(0, 0, 0, 0.60);
      overflow: hidden;
    }

    .rc-filterHeader {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 12px 12px 10px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      background: rgba(255, 255, 255, 0.02);
    }

    .rc-filterTitle {
      font-weight: 900;
      color: rgba(230, 230, 247, 0.92);
      font-size: 0.95rem;
      letter-spacing: 0.2px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .rc-filterHeaderActions {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 0 0 auto;
    }

    .rc-filterSmallBtn {
      border: 1px solid rgba(255, 255, 255, 0.14);
      background: rgba(255, 255, 255, 0.04);
      color: rgba(230, 230, 247, 0.86);
      padding: 7px 10px;
      border-radius: 12px;
      font-weight: 800;
      font-size: 0.82rem;
      cursor: pointer;
      transition: transform 120ms ease, background 120ms ease, border-color 120ms ease;
    }

    .rc-filterSmallBtn:hover {
      transform: translateY(-1px);
      background: rgba(255, 255, 255, 0.06);
      border-color: rgba(48, 216, 255, 0.28);
    }

    .rc-filterCloseBtn {
      border: 1px solid rgba(255, 255, 255, 0.14);
      background: rgba(255, 255, 255, 0.04);
      color: rgba(230, 230, 247, 0.86);
      width: 34px;
      height: 34px;
      border-radius: 12px;
      cursor: pointer;
      font-size: 1.05rem;
      line-height: 1;
      display: grid;
      place-items: center;
      transition: transform 120ms ease, background 120ms ease, border-color 120ms ease;
    }

    .rc-filterCloseBtn:hover {
      transform: translateY(-1px);
      background: rgba(255, 255, 255, 0.06);
      border-color: rgba(255, 77, 79, 0.35);
    }

    .rc-filterBody {
      padding: 12px;
    }

    .rc-filterInput {
      width: 100%;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.10);
      color: rgba(230, 230, 247, 0.92);
      border-radius: 14px;
      padding: 10px 12px;
      outline: none;
      transition: border-color 120ms ease, box-shadow 120ms ease;
    }

    .rc-filterInput::placeholder {
      color: rgba(230, 230, 247, 0.45);
    }

    .rc-filterInput:focus {
      border-color: rgba(48, 216, 255, 0.55);
      box-shadow: 0 0 0 3px rgba(48, 216, 255, 0.12);
    }

    .rc-filterHint {
      margin-top: 10px;
      font-size: 0.82rem;
      color: rgba(230, 230, 247, 0.55);
    }

    .rc-filterList {
      display: flex;
      flex-direction: column;
      margin-top: 10px;
      gap: 8px;
      max-height: 260px;
      overflow: auto;
      padding-right: 6px;
    }

    .rc-filterOption {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 10px 10px;
      border-radius: 14px;
      border: 1px solid rgba(255, 255, 255, 0.10);
      background: rgba(255, 255, 255, 0.03);
      cursor: pointer;
      transition: transform 120ms ease, background 120ms ease, border-color 120ms ease;
    }

    .rc-filterOption:hover {
      transform: translateY(-1px);
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(48, 216, 255, 0.25);
    }

    .rc-filterOption input {
      margin: 0;
      width: 16px;
      height: 16px;
      accent-color: rgba(48, 216, 255, 0.95);
      flex: 0 0 auto;
    }

    .rc-filterOptionText {
      display: flex;
      flex-direction: column;
      gap: 2px;
      flex: 1 1 auto;
      min-width: 0;
    }

    .rc-filterOptionLabel {
      font-weight: 900;
      color: rgba(230, 230, 247, 0.92);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .rc-filterOptionMeta {
      font-size: 0.78rem;
      color: rgba(230, 230, 247, 0.55);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .rc-filterCountPill {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 2px 10px;
      border-radius: 999px;
      border: 1px solid rgba(255, 255, 255, 0.10);
      background: rgba(0, 0, 0, 0.12);
      color: rgba(230, 230, 247, 0.72);
      font-variant-numeric: tabular-nums;
      flex: 0 0 auto;
      min-width: 44px;
    }

    .rc-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 44px 18px 54px;
      gap: 10px;
      color: rgba(230, 230, 247, 0.72);
      min-height: 100%;
    }

    .rc-stateTitle {
      font-weight: 900;
      font-size: 1.05rem;
      color: rgba(230, 230, 247, 0.92);
    }

    .rc-stateSubtitle {
      font-size: 0.92rem;
      color: rgba(230, 230, 247, 0.65);
      max-width: 520px;
      text-align: center;
    }

    .rc-stateActions {
      margin-top: 6px;
    }

    .rc-spinner {
      width: 42px;
      height: 42px;
      border-radius: 999px;
      border: 3px solid rgba(255, 255, 255, 0.14);
      border-top-color: rgba(48, 216, 255, 0.8);
      animation: rcSpin 900ms linear infinite;
    }

    @keyframes rcSpin {
      to {
        transform: rotate(360deg);
      }
    }

    .rc-tableWrap::-webkit-scrollbar {
      height: 12px;
      width: 12px;
    }

    .rc-tableWrap::-webkit-scrollbar-thumb {
      background: rgba(230, 230, 247, 0.20);
      border-radius: 999px;
      border: 3px solid rgba(0, 0, 0, 0);
      background-clip: padding-box;
    }

    .rc-tableWrap::-webkit-scrollbar-thumb:hover {
      background: rgba(230, 230, 247, 0.30);
      border: 3px solid rgba(0, 0, 0, 0);
      background-clip: padding-box;
    }

    .rc-tableWrap::-webkit-scrollbar-corner {
      background: transparent;
    }

    /* Responsive table sizing (no internal scroll on laptops) */
    @media (max-width: 1750px) {
      .rc-cardHeader {
        padding: 18px 18px 14px;
      }

      .rc-searchInput {
        width: min(360px, 100%);
      }

      .rc-btn {
        padding: 9px 12px;
        border-radius: 13px;
      }

      .rc-table {
        font-size: 13px;
      }

      .rc-thBtn {
        padding: 10px 34px 10px 12px;
        min-height: 52px;
      }

      .rc-thDot {
        right: 12px;
        width: 9px;
        height: 9px;
      }

      .rc-table tbody td {
        padding: 12px 12px;
      }

      .rc-idPill,
      .rc-pill {
        padding: 3px 9px;
        min-width: 40px;
      }

      .rc-table thead th:nth-child(1) { width: 80px !important; }
      .rc-table thead th:nth-child(2) { width: 120px !important; }
      .rc-table thead th:nth-child(3) { width: 90px !important; }
      .rc-table thead th:nth-child(4) { width: 180px !important; }
      .rc-table thead th:nth-child(5) { width: 180px !important; }
      .rc-table thead th:nth-child(6) { width: 90px !important; }
      .rc-table thead th:nth-child(7) { width: 80px !important; }
      .rc-table thead th:nth-child(8) { width: 145px !important; }
      .rc-table thead th:nth-child(9) { width: 190px !important; }
      .rc-table thead th:nth-child(10) { width: 125px !important; }
      .rc-table thead th:nth-child(11) { width: 200px !important; }
    }

    @media (max-width: 1500px) {
      .rc-cardHeader {
        padding: 16px 16px 12px;
      }

      .rc-title {
        font-size: 1.3rem;
      }

      .rc-searchInput {
        width: min(320px, 100%);
        padding: 9px 11px;
      }

      .rc-btn {
        padding: 9px 11px;
      }

      .rc-table {
        font-size: 12px;
      }

      .rc-thBtn {
        padding: 9px 30px 9px 10px;
        min-height: 50px;
      }

      .rc-thLabel {
        font-size: 0.9rem;
      }

      .rc-table tbody td {
        padding: 10px 10px;
      }

      .rc-table thead th:nth-child(1) { width: 70px !important; }
      .rc-table thead th:nth-child(2) { width: 105px !important; }
      .rc-table thead th:nth-child(3) { width: 80px !important; }
      .rc-table thead th:nth-child(4) { width: 160px !important; }
      .rc-table thead th:nth-child(5) { width: 160px !important; }
      .rc-table thead th:nth-child(6) { width: 80px !important; }
      .rc-table thead th:nth-child(7) { width: 70px !important; }
      .rc-table thead th:nth-child(8) { width: 120px !important; }
      .rc-table thead th:nth-child(9) { width: 160px !important; }
      .rc-table thead th:nth-child(10) { width: 110px !important; }
      .rc-table thead th:nth-child(11) { width: 200px !important; }
    }

    @media (max-width: 1320px) {
      .rc-title {
        font-size: 1.22rem;
      }

      .rc-table {
        font-size: 11.5px;
      }

      .rc-thBtn {
        padding: 8px 28px 8px 10px;
        min-height: 48px;
      }

      .rc-thSub {
        font-size: 0.74rem;
      }

      .rc-table tbody td {
        padding: 9px 9px;
      }

      .rc-table thead th:nth-child(1) { width: 64px !important; }
      .rc-table thead th:nth-child(2) { width: 96px !important; }
      .rc-table thead th:nth-child(3) { width: 70px !important; }
      .rc-table thead th:nth-child(4) { width: 140px !important; }
      .rc-table thead th:nth-child(5) { width: 140px !important; }
      .rc-table thead th:nth-child(6) { width: 70px !important; }
      .rc-table thead th:nth-child(7) { width: 64px !important; }
      .rc-table thead th:nth-child(8) { width: 105px !important; }
      .rc-table thead th:nth-child(9) { width: 140px !important; }
      .rc-table thead th:nth-child(10) { width: 96px !important; }
      .rc-table thead th:nth-child(11) { width: 170px !important; }
    }
  `;

  const filterModel = useMemo(() => {
    const key = activeFilter?.key;
    if (!key) return null;

    if (key === "features") {
      return {
        key,
        title: "Features",
        type: "text",
        placeholder: "Contiene…",
        hint: "Filtra por texto dentro de features.",
      };
    }

    if (key === "description") {
      return {
        key,
        title: "Description",
        type: "text",
        placeholder: "Contiene…",
        hint: "Filtra por texto dentro de la descripción.",
      };
    }

    if (key === "id") {
      return {
        key,
        title: "ID",
        type: "set",
        options: idOptions.map((id) => ({ value: id, label: `#${id}`, count: 1 })),
      };
    }

    if (key === "roomNumbers") {
      return {
        key,
        title: "Room number",
        type: "set",
        options: roomNumberStats.options.map((o) => ({
          value: o.key,
          label: o.name,
          count: roomNumberStats.counts.get(o.key) || 0,
        })),
      };
    }

    if (key === "floors") {
      return {
        key,
        title: "Floor",
        type: "set",
        options: floorStats.options.map((n) => ({
          value: n,
          label: String(n),
          count: floorStats.counts.get(n) || 0,
        })),
      };
    }

    if (key === "categories") {
      return {
        key,
        title: "Category",
        type: "set",
        options: categoryStats.options.map((cat) => {
          const buName = cat.businessUnitId
            ? businessUnitById.get(String(cat.businessUnitId))?.name
            : null;
          const meta = buName
            ? `ID ${cat.id} · ${buName}`
            : cat.businessUnitId
              ? `ID ${cat.id} · BU ${cat.businessUnitId}`
              : `ID ${cat.id}`;

          return {
            value: cat.id,
            label: cat.name,
            meta,
            count: categoryStats.counts.get(cat.id) || 0,
          };
        }),
      };
    }

    if (key === "businessUnits") {
      return {
        key,
        title: "Business unit",
        type: "set",
        options: businessUnitStats.options.map((bu) => ({
          value: bu.id,
          label: bu.name,
          meta: `ID ${bu.id}`,
          count: businessUnitStats.counts.get(bu.id) || 0,
        })),
      };
    }

    if (key === "sqMeters") {
      return {
        key,
        title: "m²",
        type: "set",
        options: sqMetersStats.options.map((n) => ({
          value: n,
          label: String(n),
          count: sqMetersStats.counts.get(n) || 0,
        })),
      };
    }

    if (key === "beds") {
      return {
        key,
        title: "Beds",
        type: "set",
        options: bedsStats.options.map((n) => ({
          value: n,
          label: String(n),
          count: bedsStats.counts.get(n) || 0,
        })),
      };
    }

    if (key === "bedTypes") {
      return {
        key,
        title: "Bed type",
        type: "set",
        options: bedTypeStats.options.map((o) => ({
          value: o.key,
          label: o.name,
          count: bedTypeStats.counts.get(o.key) || 0,
        })),
      };
    }

    if (key === "views") {
      return {
        key,
        title: "View",
        type: "set",
        options: viewStats.options.map((o) => ({
          value: o.key,
          label: o.name,
          count: viewStats.counts.get(o.key) || 0,
        })),
      };
    }

    return null;
  }, [
    activeFilter?.key,
    bedTypeStats,
    bedsStats,
    businessUnitById,
    businessUnitStats,
    categoryStats,
    floorStats,
    idOptions,
    roomNumberStats,
    sqMetersStats,
    viewStats,
  ]);

  const filterPopoverPos = useMemo(() => {
    if (!activeFilter?.rect) return null;

    const rect = activeFilter.rect;
    const pad = 12;
    const viewportW = window.innerWidth || 1200;
    const viewportH = window.innerHeight || 800;

    const width = Math.min(380, viewportW - pad * 2);
    const estimatedHeight = 420;

    let left = rect.left;
    left = Math.max(pad, Math.min(left, viewportW - width - pad));

    const showBelow = rect.bottom + 10 + estimatedHeight <= viewportH - pad;
    let top = showBelow ? rect.bottom + 10 : rect.top - estimatedHeight - 10;
    top = Math.max(pad, Math.min(top, viewportH - estimatedHeight - pad));

    return { top, left, width };
  }, [activeFilter?.rect]);

  const visibleFilterOptions = useMemo(() => {
    if (!filterModel || filterModel.type !== "set") return [];
    const needle = filterOptionQuery.trim().toLowerCase();
    if (!needle) return filterModel.options;

    return filterModel.options.filter((o) => {
      const label = String(o.label || "").toLowerCase();
      const meta = String(o.meta || "").toLowerCase();
      return label.includes(needle) || meta.includes(needle);
    });
  }, [filterModel, filterOptionQuery]);

  const selectAllVisibleOptions = () => {
    if (!filterModel || filterModel.type !== "set") return;
    setColumnFilters((prev) => ({
      ...prev,
      [filterModel.key]: visibleFilterOptions.map((o) => o.value),
    }));
  };

  // Fetch business units and categories
  useEffect(() => {
    async function fetchData() {
      const token = localStorage.getItem("palantir_token");
      try {
        const [bus, cats] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/api/business-units/all`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/api/room_categories`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        setBusinessUnits(bus.data);
        setRoomCategories(cats.data);
      } catch (err) {
        setBusinessUnits([]);
        setRoomCategories([]);
      }
    }
    fetchData();
  }, []);

  // Fetch rooms
  useEffect(() => {
    async function fetchRooms() {
      try {
        const token = localStorage.getItem("palantir_token");
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/rooms`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRooms(res.data);
      } catch (err) {
        setRooms([]);
      } finally {
        setLoading(false);
      }
    }
    fetchRooms();
  }, []);

  // POST room
  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      const token = localStorage.getItem("palantir_token");
      await axios.post(`${process.env.REACT_APP_API_URL}/api/rooms`, newRoom, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowNewPopup(false);
      setNewRoom(defaultRoom);
      // Recargar rooms
      const updated = await axios.get(`${process.env.REACT_APP_API_URL}/api/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRooms(updated.data);
    } catch (err) {
      setError("Error saving room.");
    } finally {
      setSaving(false);
    }
  }

  // DELETE room
  async function handleDelete(e) {
    e.preventDefault();
    if (!deleteId) return;
    setDeleting(true);
    setDeleteError("");
    try {
      const token = localStorage.getItem("palantir_token");
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/rooms/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowDeletePopup(false);
      // Refresca lista
      const updated = await axios.get(`${process.env.REACT_APP_API_URL}/api/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRooms(updated.data);
    } catch (err) {
      setDeleteError("No se pudo borrar la Room.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="rc-page">
      <style>{uiCss}</style>

      <div className="rc-card">
        <div className="rc-cardHeader">
          <div>
            <h2 className="rc-title">Rooms</h2>
            <div className="rc-subtitle">
              {loading
                ? "Cargando rooms…"
                : rooms.length === 0
                  ? "Aún no hay rooms."
                  : `${filteredRooms.length} de ${rooms.length} rooms`}
            </div>
          </div>

          <div className="rc-actions">
            <input
              className="rc-searchInput"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por ID, número, categoría o descripción…"
              disabled={loading}
            />

            <button
              type="button"
              className="rc-btn rc-btn--primary"
              onClick={() => {
                setShowNewPopup(true);
                setError("");
                setNewRoom(defaultRoom);
              }}
            >
              + Add Room
            </button>

            <button
              type="button"
              className="rc-btn rc-btn--danger"
              onClick={() => {
                setShowDeletePopup(true);
                setDeleteError("");
                setDeleteId("");
              }}
            >
              Delete Room
            </button>
          </div>
        </div>

        <div className="rc-tableWrap" ref={tableWrapRef}>
          {loading ? (
            <div className="rc-state">
              <div className="rc-spinner" aria-hidden="true" />
              <div className="rc-stateSubtitle">Cargando rooms…</div>
            </div>
          ) : filteredRooms.length === 0 ? (
            <div className="rc-state">
              <div className="rc-stateTitle">
                {rooms.length === 0 ? "No hay rooms." : "Sin resultados."}
              </div>
              <div className="rc-stateSubtitle">
                {rooms.length === 0
                  ? "Crea tu primera habitación para empezar."
                  : "Prueba otra búsqueda o limpia el filtro."}
              </div>
              {searchQuery.trim() ? (
                <div className="rc-stateActions">
                  <button
                    type="button"
                    className="rc-btn rc-btn--ghost"
                    onClick={() => setSearchQuery("")}
                  >
                    Limpiar búsqueda
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <table ref={tableRef} className="rc-table" style={{ zoom: tableZoom }}>
              <thead>
                <tr>
                  <th style={{ width: "90px" }}>
                    <button
                      type="button"
                      data-rc-filter-trigger="id"
                      className={`rc-thBtn ${columnIsFiltered("id") ? "rc-thBtn--filtered" : ""} ${
                        activeFilter?.key === "id" ? "rc-thBtn--open" : ""
                      }`}
                      onClick={(e) => toggleFilterPopover("id", e.currentTarget)}
                    >
                      <span className="rc-thLabel">ID</span>
                      <span className="rc-thSub">{columnSubtitle("id")}</span>
                      <span
                        className={`rc-thDot ${columnIsFiltered("id") ? "rc-thDot--on" : ""}`}
                        aria-hidden="true"
                      />
                    </button>
                  </th>

                  <th style={{ width: "140px" }}>
                    <button
                      type="button"
                      data-rc-filter-trigger="roomNumbers"
                      className={`rc-thBtn ${
                        columnIsFiltered("roomNumbers") ? "rc-thBtn--filtered" : ""
                      } ${activeFilter?.key === "roomNumbers" ? "rc-thBtn--open" : ""}`}
                      onClick={(e) => toggleFilterPopover("roomNumbers", e.currentTarget)}
                    >
                      <span className="rc-thLabel">Room #</span>
                      <span className="rc-thSub">{columnSubtitle("roomNumbers")}</span>
                      <span
                        className={`rc-thDot ${
                          columnIsFiltered("roomNumbers") ? "rc-thDot--on" : ""
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                  </th>

                  <th style={{ width: "110px" }}>
                    <button
                      type="button"
                      data-rc-filter-trigger="floors"
                      className={`rc-thBtn rc-thBtn--right ${
                        columnIsFiltered("floors") ? "rc-thBtn--filtered" : ""
                      } ${activeFilter?.key === "floors" ? "rc-thBtn--open" : ""}`}
                      onClick={(e) => toggleFilterPopover("floors", e.currentTarget)}
                    >
                      <span className="rc-thLabel">Floor</span>
                      <span className="rc-thSub">{columnSubtitle("floors")}</span>
                      <span
                        className={`rc-thDot ${columnIsFiltered("floors") ? "rc-thDot--on" : ""}`}
                        aria-hidden="true"
                      />
                    </button>
                  </th>

                  <th style={{ width: "220px" }}>
                    <button
                      type="button"
                      data-rc-filter-trigger="categories"
                      className={`rc-thBtn ${
                        columnIsFiltered("categories") ? "rc-thBtn--filtered" : ""
                      } ${activeFilter?.key === "categories" ? "rc-thBtn--open" : ""}`}
                      onClick={(e) => toggleFilterPopover("categories", e.currentTarget)}
                    >
                      <span className="rc-thLabel">Category</span>
                      <span className="rc-thSub">{columnSubtitle("categories")}</span>
                      <span
                        className={`rc-thDot ${
                          columnIsFiltered("categories") ? "rc-thDot--on" : ""
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                  </th>

                  <th style={{ width: "220px" }}>
                    <button
                      type="button"
                      data-rc-filter-trigger="businessUnits"
                      className={`rc-thBtn ${
                        columnIsFiltered("businessUnits") ? "rc-thBtn--filtered" : ""
                      } ${activeFilter?.key === "businessUnits" ? "rc-thBtn--open" : ""}`}
                      onClick={(e) => toggleFilterPopover("businessUnits", e.currentTarget)}
                    >
                      <span className="rc-thLabel">Business unit</span>
                      <span className="rc-thSub">{columnSubtitle("businessUnits")}</span>
                      <span
                        className={`rc-thDot ${
                          columnIsFiltered("businessUnits") ? "rc-thDot--on" : ""
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                  </th>

                  <th style={{ width: "100px" }}>
                    <button
                      type="button"
                      data-rc-filter-trigger="sqMeters"
                      className={`rc-thBtn rc-thBtn--right ${
                        columnIsFiltered("sqMeters") ? "rc-thBtn--filtered" : ""
                      } ${activeFilter?.key === "sqMeters" ? "rc-thBtn--open" : ""}`}
                      onClick={(e) => toggleFilterPopover("sqMeters", e.currentTarget)}
                    >
                      <span className="rc-thLabel">m²</span>
                      <span className="rc-thSub">{columnSubtitle("sqMeters")}</span>
                      <span
                        className={`rc-thDot ${
                          columnIsFiltered("sqMeters") ? "rc-thDot--on" : ""
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                  </th>

                  <th style={{ width: "90px" }}>
                    <button
                      type="button"
                      data-rc-filter-trigger="beds"
                      className={`rc-thBtn rc-thBtn--right ${
                        columnIsFiltered("beds") ? "rc-thBtn--filtered" : ""
                      } ${activeFilter?.key === "beds" ? "rc-thBtn--open" : ""}`}
                      onClick={(e) => toggleFilterPopover("beds", e.currentTarget)}
                    >
                      <span className="rc-thLabel">Beds</span>
                      <span className="rc-thSub">{columnSubtitle("beds")}</span>
                      <span
                        className={`rc-thDot ${columnIsFiltered("beds") ? "rc-thDot--on" : ""}`}
                        aria-hidden="true"
                      />
                    </button>
                  </th>

                  <th style={{ width: "170px" }}>
                    <button
                      type="button"
                      data-rc-filter-trigger="bedTypes"
                      className={`rc-thBtn ${
                        columnIsFiltered("bedTypes") ? "rc-thBtn--filtered" : ""
                      } ${activeFilter?.key === "bedTypes" ? "rc-thBtn--open" : ""}`}
                      onClick={(e) => toggleFilterPopover("bedTypes", e.currentTarget)}
                    >
                      <span className="rc-thLabel">Bed type</span>
                      <span className="rc-thSub">{columnSubtitle("bedTypes")}</span>
                      <span
                        className={`rc-thDot ${
                          columnIsFiltered("bedTypes") ? "rc-thDot--on" : ""
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                  </th>

                  <th style={{ width: "230px" }}>
                    <button
                      type="button"
                      data-rc-filter-trigger="features"
                      className={`rc-thBtn ${columnIsFiltered("features") ? "rc-thBtn--filtered" : ""} ${
                        activeFilter?.key === "features" ? "rc-thBtn--open" : ""
                      }`}
                      onClick={(e) => toggleFilterPopover("features", e.currentTarget)}
                    >
                      <span className="rc-thLabel">Features</span>
                      <span className="rc-thSub">{columnSubtitle("features")}</span>
                      <span
                        className={`rc-thDot ${columnIsFiltered("features") ? "rc-thDot--on" : ""}`}
                        aria-hidden="true"
                      />
                    </button>
                  </th>

                  <th style={{ width: "160px" }}>
                    <button
                      type="button"
                      data-rc-filter-trigger="views"
                      className={`rc-thBtn ${columnIsFiltered("views") ? "rc-thBtn--filtered" : ""} ${
                        activeFilter?.key === "views" ? "rc-thBtn--open" : ""
                      }`}
                      onClick={(e) => toggleFilterPopover("views", e.currentTarget)}
                    >
                      <span className="rc-thLabel">View</span>
                      <span className="rc-thSub">{columnSubtitle("views")}</span>
                      <span
                        className={`rc-thDot ${columnIsFiltered("views") ? "rc-thDot--on" : ""}`}
                        aria-hidden="true"
                      />
                    </button>
                  </th>

                  <th style={{ width: "260px" }}>
                    <button
                      type="button"
                      data-rc-filter-trigger="description"
                      className={`rc-thBtn ${
                        columnIsFiltered("description") ? "rc-thBtn--filtered" : ""
                      } ${activeFilter?.key === "description" ? "rc-thBtn--open" : ""}`}
                      onClick={(e) => toggleFilterPopover("description", e.currentTarget)}
                    >
                      <span className="rc-thLabel">Description</span>
                      <span className="rc-thSub">{columnSubtitle("description")}</span>
                      <span
                        className={`rc-thDot ${
                          columnIsFiltered("description") ? "rc-thDot--on" : ""
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRooms.map((room) => {
                  const bu = businessUnitById.get(String(room.business_unit_id));
                  const cat = roomCategoryById.get(String(room.room_category_id));

                  const categoryName = cat?.name ?? `Category ${room.room_category_id}`;
                  const categoryMetaParts = [];
                  if (room.room_category_id != null && String(room.room_category_id).trim()) {
                    categoryMetaParts.push(`ID ${room.room_category_id}`);
                  }
                  if (cat?.business_unit_id != null && String(cat.business_unit_id).trim()) {
                    const catBu = businessUnitById.get(String(cat.business_unit_id));
                    categoryMetaParts.push(catBu?.name ?? `BU ${cat.business_unit_id}`);
                  }

                  return (
                    <tr key={room.id}>
                      <td>
                        <span className="rc-idPill">#{room.id}</span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="rc-nameBtn"
                          title="Edit"
                          onClick={() => openEditPopup(room)}
                        >
                          {room.room_number || <span className="rc-muted">—</span>}
                        </button>
                      </td>
                      <td className="rc-numCell">
                        <span className="rc-pill">
                          {room.floor ?? <span className="rc-muted">—</span>}
                        </span>
                      </td>
                      <td>
                        <div className="rc-buCell">
                          <div className="rc-buName">{categoryName}</div>
                          {categoryMetaParts.length ? (
                            <div className="rc-buMeta">{categoryMetaParts.join(" · ")}</div>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        <div className="rc-buCell">
                          <div className="rc-buName">
                            {bu?.name ?? `Business Unit ${room.business_unit_id}`}
                          </div>
                          {bu?.name ? (
                            <div className="rc-buMeta">ID {room.business_unit_id}</div>
                          ) : null}
                        </div>
                      </td>
                      <td className="rc-numCell">
                        <span className="rc-pill">
                          {room.sq_meters ?? <span className="rc-muted">—</span>}
                        </span>
                      </td>
                      <td className="rc-numCell">
                        <span className="rc-pill">
                          {room.num_beds ?? <span className="rc-muted">—</span>}
                        </span>
                      </td>
                      <td>
                        <div className="rc-desc" title={room.bed_type || ""}>
                          {room.bed_type || <span className="rc-muted">—</span>}
                        </div>
                      </td>
                      <td>
                        <div className="rc-desc" title={room.features || ""}>
                          {room.features || <span className="rc-muted">—</span>}
                        </div>
                      </td>
                      <td>
                        <div className="rc-desc" title={room.view || ""}>
                          {room.view || <span className="rc-muted">—</span>}
                        </div>
                      </td>
                      <td>
                        <div className="rc-desc" title={room.description || ""}>
                          {room.description || <span className="rc-muted">—</span>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {activeFilter?.key && filterModel && filterPopoverPos ? (
          <div
            ref={filterPopoverRef}
            className="rc-filterPopover"
            style={{ top: filterPopoverPos.top, left: filterPopoverPos.left, width: filterPopoverPos.width }}
          >
            <div className="rc-filterHeader">
              <div className="rc-filterTitle">{filterModel.title}</div>
              <div className="rc-filterHeaderActions">
                {columnIsFiltered(filterModel.key) ? (
                  <button
                    type="button"
                    className="rc-filterSmallBtn"
                    onClick={() => clearColumnFilter(filterModel.key)}
                  >
                    Clear
                  </button>
                ) : null}

                {filterModel.type === "set" ? (
                  <button type="button" className="rc-filterSmallBtn" onClick={selectAllVisibleOptions}>
                    All
                  </button>
                ) : null}

                <button type="button" className="rc-filterCloseBtn" onClick={closeFilterPopover}>
                  ×
                </button>
              </div>
            </div>

            <div className="rc-filterBody">
              {filterModel.type === "text" ? (
                <>
                  <input
                    className="rc-filterInput"
                    value={String(columnFilters[filterModel.key] || "")}
                    onChange={(e) =>
                      setColumnFilters((prev) => ({ ...prev, [filterModel.key]: e.target.value }))
                    }
                    placeholder={filterModel.placeholder || "Contiene…"}
                    autoFocus
                  />
                  {filterModel.hint ? <div className="rc-filterHint">{filterModel.hint}</div> : null}
                </>
              ) : (
                <>
                  <input
                    className="rc-filterInput"
                    value={filterOptionQuery}
                    onChange={(e) => setFilterOptionQuery(e.target.value)}
                    placeholder={`Buscar en ${filterModel.title.toLowerCase()}…`}
                    autoFocus
                  />

                  <div className="rc-filterList">
                    {visibleFilterOptions.length === 0 ? (
                      <div className="rc-filterHint">No hay opciones que coincidan.</div>
                    ) : (
                      visibleFilterOptions.map((opt) => {
                        const selected = Array.isArray(columnFilters[filterModel.key])
                          ? columnFilters[filterModel.key].includes(opt.value)
                          : false;

                        return (
                          <label key={String(opt.value)} className="rc-filterOption" title={opt.label}>
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => toggleMultiSelectFilter(filterModel.key, opt.value)}
                            />
                            <span className="rc-filterOptionText">
                              <div className="rc-filterOptionLabel">{opt.label}</div>
                              {opt.meta ? <div className="rc-filterOptionMeta">{opt.meta}</div> : null}
                            </span>
                            <span className="rc-filterCountPill">{opt.count}</span>
                          </label>
                        );
                      })
                    )}
                  </div>

                  <div className="rc-filterHint">Selecciona uno o varios valores.</div>
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {/* Popup Add Room */}
      {showNewPopup && (
        <div style={{
            position: "fixed", left: 0, top: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 1001,
            display: "flex", alignItems: "center", justifyContent: "center"
        }}>
            <div style={{
            background: "#191933", color: "#fff", borderRadius: 10, padding: 32, minWidth: 390, boxShadow: "0 2px 14px #0009",
            border: "2px solid #333", position: "relative"
            }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Add Room</h4>
                <button onClick={() => setShowNewPopup(false)}
                style={{
                    background: "none", border: "none", color: "#fff", fontSize: 26, fontWeight: 400, cursor: "pointer"
                }}>×</button>
            </div>
            <form onSubmit={e => { e.preventDefault(); handleSave(); }}>
                <div className="mb-2">
                <label>Business Unit</label>
                <select
                    className="form-control form-control-sm"
                    value={newRoom.business_unit_id}
                    onChange={e =>
                    setNewRoom(r => ({
                        ...r,
                        business_unit_id: e.target.value,
                        room_category_id: "" // Limpiar categoría si cambia business unit
                    }))
                    }
                    required
                >
                    <option value="">-- Select --</option>
                    {businessUnits.map(bu => (
                    <option value={bu.id} key={bu.id}>{bu.id} – {bu.name}</option>
                    ))}
                </select>
                </div>
                <div className="mb-2">
                <label>Room Category</label>
                <select
                    className="form-control form-control-sm"
                    value={newRoom.room_category_id}
                    onChange={e => setNewRoom(r => ({ ...r, room_category_id: e.target.value }))}
                    required
                    disabled={!newRoom.business_unit_id} // Bloquea hasta elegir business unit
                >
                    <option value="">-- Select --</option>
                    {roomCategories
                    .filter(cat => String(cat.business_unit_id) === String(newRoom.business_unit_id))
                    .map(cat => (
                        <option value={cat.id} key={cat.id}>{cat.id} – {cat.name}</option>
                    ))}
                </select>
                </div>
                <div className="mb-2">
                <label>Room Number</label>
                <input className="form-control form-control-sm" value={newRoom.room_number} onChange={e => setNewRoom(r => ({ ...r, room_number: e.target.value }))} required />
                </div>
                <div className="mb-2">
                <label>Floor</label>
                <input type="number" className="form-control form-control-sm" value={newRoom.floor} onChange={e => setNewRoom(r => ({ ...r, floor: e.target.value }))} required />
                </div>
                <div className="mb-2">
                <label>m²</label>
                <input type="number" className="form-control form-control-sm" value={newRoom.sq_meters} onChange={e => setNewRoom(r => ({ ...r, sq_meters: e.target.value }))} required />
                </div>
                <div className="mb-2">
                <label>Beds</label>
                <input type="number" className="form-control form-control-sm" value={newRoom.num_beds} onChange={e => setNewRoom(r => ({ ...r, num_beds: e.target.value }))} required />
                </div>
                <div className="mb-2">
                <label>Bed Type</label>
                <input className="form-control form-control-sm" value={newRoom.bed_type} onChange={e => setNewRoom(r => ({ ...r, bed_type: e.target.value }))} required />
                </div>
                <div className="mb-2">
                <label>Features</label>
                <input className="form-control form-control-sm" value={newRoom.features} onChange={e => setNewRoom(r => ({ ...r, features: e.target.value }))} />
                </div>
                <div className="mb-2">
                <label>View</label>
                <input className="form-control form-control-sm" value={newRoom.view} onChange={e => setNewRoom(r => ({ ...r, view: e.target.value }))} />
                </div>
                <div className="mb-2">
                <label>Description</label>
                <textarea className="form-control form-control-sm" value={newRoom.description} onChange={e => setNewRoom(r => ({ ...r, description: e.target.value }))} rows={2} />
                </div>
                {error && <div className="text-danger mb-2">{error}</div>}
                <div className="d-flex gap-2 mt-4 justify-content-end">
                <button type="button" className="btn btn-secondary" onClick={() => setShowNewPopup(false)} disabled={saving}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Creating..." : "Save"}</button>
                </div>
            </form>
            </div>
        </div>
        )}

        {/* Popup Edit Room */}
          {showEditPopup && editRoom && (
            <div style={{
              position: "fixed", left: 0, top: 0, width: "100vw", height: "100vh",
              background: "rgba(0,0,0,0.5)", zIndex: 1001,
              display: "flex", alignItems: "center", justifyContent: "center"
            }}>
              <div style={{
                background: "#191933", color: "#fff", borderRadius: 10, padding: 32,
                minWidth: 390, boxShadow: "0 2px 14px #0009",
                border: "2px solid #333", position: "relative"
              }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="mb-0">Edit Room</h4>
                  <button onClick={() => setShowEditPopup(false)}
                    style={{
                      background: "none", border: "none", color: "#fff",
                      fontSize: 26, fontWeight: 400, cursor: "pointer"
                    }}>×</button>
                </div>

                <form onSubmit={async (e) => {
                  e.preventDefault();
                  setEditing(true);
                  setEditError("");
                  try {
                    const token = localStorage.getItem("palantir_token");
                    await axios.put(
                      `${process.env.REACT_APP_API_URL}/api/rooms/${editRoom.id}`,
                      editRoom,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    setShowEditPopup(false);

                    // Refrescar lista de rooms
                    const updated = await axios.get(`${process.env.REACT_APP_API_URL}/api/rooms`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    setRooms(updated.data);
                  } catch (err) {
                    console.error("[RoomsList] Error updating room:", err);
                    setEditError("Error updating room.");
                  } finally {
                    setEditing(false);
                  }
                }}>
                  <div className="mb-2">
                    <label>Business Unit</label>
                    <select
                      className="form-control form-control-sm"
                      value={editRoom.business_unit_id}
                      onChange={e => setEditRoom(r => ({
                        ...r,
                        business_unit_id: e.target.value,
                        room_category_id: ""
                      }))}
                      required
                    >
                      <option value="">-- Select --</option>
                      {businessUnits.map(bu => (
                        <option value={bu.id} key={bu.id}>{bu.id} – {bu.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-2">
                    <label>Room Category</label>
                    <select
                      className="form-control form-control-sm"
                      value={editRoom.room_category_id}
                      onChange={e => setEditRoom(r => ({ ...r, room_category_id: e.target.value }))}
                      required
                      disabled={!editRoom.business_unit_id}
                    >
                      <option value="">-- Select --</option>
                      {roomCategories
                        .filter(cat => String(cat.business_unit_id) === String(editRoom.business_unit_id))
                        .map(cat => (
                          <option value={cat.id} key={cat.id}>{cat.id} – {cat.name}</option>
                        ))}
                    </select>
                  </div>

                  <div className="mb-2">
                    <label>Room Number</label>
                    <input className="form-control form-control-sm"
                      value={editRoom.room_number}
                      onChange={e => setEditRoom(r => ({ ...r, room_number: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="mb-2">
                    <label>Floor</label>
                    <input type="number" className="form-control form-control-sm"
                      value={editRoom.floor}
                      onChange={e => setEditRoom(r => ({ ...r, floor: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="mb-2">
                    <label>m²</label>
                    <input type="number" className="form-control form-control-sm"
                      value={editRoom.sq_meters}
                      onChange={e => setEditRoom(r => ({ ...r, sq_meters: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="mb-2">
                    <label>Beds</label>
                    <input type="number" className="form-control form-control-sm"
                      value={editRoom.num_beds}
                      onChange={e => setEditRoom(r => ({ ...r, num_beds: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="mb-2">
                    <label>Bed Type</label>
                    <input className="form-control form-control-sm"
                      value={editRoom.bed_type}
                      onChange={e => setEditRoom(r => ({ ...r, bed_type: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="mb-2">
                    <label>Features</label>
                    <input className="form-control form-control-sm"
                      value={editRoom.features}
                      onChange={e => setEditRoom(r => ({ ...r, features: e.target.value }))}
                    />
                  </div>

                  <div className="mb-2">
                    <label>View</label>
                    <input className="form-control form-control-sm"
                      value={editRoom.view}
                      onChange={e => setEditRoom(r => ({ ...r, view: e.target.value }))}
                    />
                  </div>

                  <div className="mb-2">
                    <label>Description</label>
                    <textarea className="form-control form-control-sm"
                      value={editRoom.description}
                      onChange={e => setEditRoom(r => ({ ...r, description: e.target.value }))}
                      rows={2}
                    />
                  </div>

                  {editError && <div className="text-danger mb-2">{editError}</div>}

                  <div className="d-flex gap-2 mt-4 justify-content-end">
                    <button type="button" className="btn btn-secondary"
                      onClick={() => setShowEditPopup(false)} disabled={editing}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-success" disabled={editing}>
                      {editing ? "Updating..." : "Save Changes"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}



      {/* Popup Delete Room */}
      {showDeletePopup && (
        <div style={{
          position: "fixed", left: 0, top: 0, width: "100vw", height: "100vh", background: "rgba(0,0,0,0.5)", zIndex: 1001,
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>
          <div style={{
            background: "#191933", color: "#fff", borderRadius: 10, padding: 32, minWidth: 390, boxShadow: "0 2px 14px #0009",
            border: "2px solid #333", position: "relative"
          }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">Delete Room</h4>
              <button onClick={() => setShowDeletePopup(false)}
                style={{
                  background: "none", border: "none", color: "#fff", fontSize: 26, fontWeight: 400, cursor: "pointer"
                }}>×</button>
            </div>
            <form onSubmit={handleDelete}>
              <div className="mb-2">
                <label>Select Room to Delete</label>
                <select
                  className="form-control form-control-sm"
                  value={deleteId}
                  onChange={e => setDeleteId(e.target.value)}
                  required
                >
                  <option value="">-- Select --</option>
                  {rooms.map(room => (
                    <option value={room.id} key={room.id}>
                      {room.id} – {room.room_number}
                    </option>
                  ))}
                </select>
              </div>
              {deleteError && <div className="text-danger mb-2">{deleteError}</div>}
              <div className="d-flex gap-2 mt-4 justify-content-end">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeletePopup(false)} disabled={deleting}>Cancel</button>
                <button type="submit" className="btn btn-danger" disabled={deleting || !deleteId}>
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default RoomsList;
